import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Switch,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { CloseIcon, DeleteIcon, EditIcon, SearchIcon } from "@chakra-ui/icons";
import { createColumnHelper } from "@tanstack/react-table";
import { useCallback, useMemo, useRef, useState } from "react";
import WelcomeSection from "components/WelcomeSection";
import { ReactTable } from "components/ReactTable";
import { useTrackingDrivers, type TrackingDriver, type DriverCreatePayload, type DriverUpdatePayload } from "hooks/useTrackingDrivers";
import { showToast, toastTypes } from "utils";

function formatName(d: TrackingDriver) {
  const name = [d.first_name, d.last_name].filter(Boolean).join(" ");
  return name || d.username || "—";
}

export default function TrackingDriversPage() {
  const toast = useToast();
  const { drivers, isLoading, error, create, update, remove } = useTrackingDrivers();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
  const cancelRef = useRef<any>(null);

  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [editing, setEditing] = useState<TrackingDriver | null>(null);
  const [formValues, setFormValues] = useState<DriverCreatePayload & DriverUpdatePayload>({
    user_id: 0,
    license_number: "",
    is_active: true,
    institution_id: null,
    sub_area_id: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [activeItem, setActiveItem] = useState<TrackingDriver | null>(null);
  const [searchValue, setSearchValue] = useState("");

  const resetForm = useCallback(() => {
    setFormValues({
      user_id: 0,
      license_number: "",
      is_active: true,
      institution_id: null,
      sub_area_id: null,
    });
    setEditing(null);
    setFormMode("create");
  }, []);

  const openCreate = useCallback(() => {
    resetForm();
    setFormMode("create");
    onOpen();
  }, [onOpen, resetForm]);

  const openEdit = useCallback(
    (row: TrackingDriver) => {
      setEditing(row);
      setFormMode("edit");
      setFormValues((prev) => ({
        ...prev,
        license_number: row.license_number ?? "",
        is_active: row.is_active,
        institution_id: row.institution_id ?? null,
        sub_area_id: row.sub_area_id ?? null,
      }));
      onOpen();
    },
    [onOpen]
  );

  const handleSubmit = useCallback(async () => {
    if (formMode === "create" && (!formValues.user_id || formValues.user_id < 1)) {
      showToast({ toast, message: "L'ID utilisateur est obligatoire.", status: toastTypes.error });
      return;
    }
    setIsSubmitting(true);
    try {
      if (formMode === "create") {
        await create({
          user_id: formValues.user_id,
          license_number: formValues.license_number || undefined,
          is_active: formValues.is_active,
          institution_id: formValues.institution_id ?? undefined,
          sub_area_id: formValues.sub_area_id ?? undefined,
        });
        showToast({ toast, message: "Conducteur créé.", status: toastTypes.success });
      } else if (editing) {
        await update(editing.id, {
          license_number: formValues.license_number,
          is_active: formValues.is_active,
          institution_id: formValues.institution_id ?? undefined,
          sub_area_id: formValues.sub_area_id ?? undefined,
        });
        showToast({ toast, message: "Conducteur mis à jour.", status: toastTypes.success });
      }
      onClose();
      resetForm();
    } catch (e: any) {
      const msg = e?.response?.data?.user_id?.[0] || e?.response?.data?.detail || e?.message || "Erreur.";
      showToast({ toast, message: msg, status: toastTypes.error });
    } finally {
      setIsSubmitting(false);
    }
  }, [create, update, editing, formMode, formValues, onClose, resetForm, toast]);

  const handleDeleteClick = useCallback((row: TrackingDriver) => {
    setActiveItem(row);
    onConfirmOpen();
  }, [onConfirmOpen]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!activeItem) return;
    setDeletingId(activeItem.id);
    try {
      await remove(activeItem.id);
      showToast({ toast, message: "Conducteur supprimé.", status: toastTypes.success });
      onConfirmClose();
      setActiveItem(null);
    } catch (e: any) {
      showToast({ toast, message: e?.response?.data?.detail || "Erreur suppression.", status: toastTypes.error });
    } finally {
      setDeletingId(null);
    }
  }, [activeItem, remove, toast, onConfirmClose]);

  const filteredData = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    if (!q) return drivers;
    return drivers.filter(
      (d) =>
        formatName(d).toLowerCase().includes(q) ||
        (d.email && d.email.toLowerCase().includes(q)) ||
        (d.username && d.username.toLowerCase().includes(q))
    );
  }, [drivers, searchValue]);

  const columns = useMemo(() => {
    const col = createColumnHelper<TrackingDriver>();
    return [
      col.accessor((r) => formatName(r), { id: "name", header: "Nom", cell: (info) => <Text>{info.getValue()}</Text> }),
      col.accessor("email", { header: "Email", cell: (info) => <Text fontSize="sm">{info.getValue() ?? "—"}</Text> }),
      col.accessor("phone_number", { header: "Téléphone", cell: (info) => <Text fontSize="sm">{info.getValue() ?? "—"}</Text> }),
      col.accessor("license_number", { header: "Permis", cell: (info) => <Text fontSize="sm">{info.getValue() ?? "—"}</Text> }),
      col.accessor("is_active", {
        header: "Statut",
        cell: (info) => (
          <Badge colorScheme={info.getValue() ? "green" : "gray"}>{info.getValue() ? "Actif" : "Inactif"}</Badge>
        ),
      }),
      col.display({
        id: "action",
        header: "Action",
        cell: ({ row }) => (
          <HStack>
            <IconButton aria-label="Modifier" size="sm" icon={<EditIcon />} onClick={() => openEdit(row.original)} />
            <IconButton
              aria-label="Supprimer"
              size="sm"
              icon={<DeleteIcon />}
              colorScheme="red"
              onClick={() => handleDeleteClick(row.original)}
              isLoading={deletingId === row.original.id}
            />
          </HStack>
        ),
      }),
    ];
  }, [openEdit, handleDeleteClick, deletingId]);

  return (
    <>
      <Flex gap="18px" bg="very_light_gray" flex={1} flexDirection="column" py="20px">
        <WelcomeSection />
        <Flex flexDirection="column" alignItems="start" px={{ base: "20px", sm: "24px" }} minHeight="100vh">
          <Flex justifyContent="space-between" alignItems="center" w="100%" flexWrap="wrap" gap={4}>
            <Heading size="text2xl" as="h5" fontFamily="Arimo">Conducteurs</Heading>
            <Button size="xl" variant="outline" colorScheme="red_origin" onClick={openCreate}>
              Créer un conducteur
            </Button>
          </Flex>

          {error && (
            <Box mt={4} p={4} bg="red.50" borderRadius="md">
              <Text color="red.700">Erreur chargement. Vérifiez l&apos;API Tracking.</Text>
            </Box>
          )}

          <Box mt={4} bg="white" w="100%" borderRadius="10px">
            <Flex p={4} flexDirection={{ base: "column", sm: "row" }}>
              <InputGroup w={{ base: "100%", sm: "300px" }}>
                <Input
                  placeholder="Rechercher un conducteur"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
                <InputRightElement>
                  {searchValue ? <IconButton aria-label="Effacer" size="sm" icon={<CloseIcon />} onClick={() => setSearchValue("")} /> : null}
                </InputRightElement>
              </InputGroup>
            </Flex>
            {isLoading && filteredData.length === 0 ? (
              <Flex justify="center" py={10}><Spinner size="lg" /></Flex>
            ) : (
              <ReactTable columns={columns as any} data={filteredData} size="sm" />
            )}
            {!isLoading && filteredData.length === 0 && (
              <Box p={8} textAlign="center"><Text color="gray.600">Aucun conducteur.</Text></Box>
            )}
          </Box>
        </Flex>
      </Flex>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{formMode === "create" ? "Ajouter un conducteur" : "Modifier le conducteur"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              {formMode === "create" && (
                <FormControl isRequired>
                  <FormLabel>ID utilisateur (User)</FormLabel>
                  <Input
                    type="number"
                    value={formValues.user_id || ""}
                    onChange={(e) => setFormValues((p) => ({ ...p, user_id: parseInt(e.target.value, 10) || 0 }))}
                    placeholder="ID du compte utilisateur"
                  />
                </FormControl>
              )}
              <FormControl>
                <FormLabel>N° permis</FormLabel>
                <Input
                  value={formValues.license_number ?? ""}
                  onChange={(e) => setFormValues((p) => ({ ...p, license_number: e.target.value }))}
                />
              </FormControl>
              <FormControl>
                <Flex align="center"><FormLabel mb={0}>Actif</FormLabel><Switch isChecked={formValues.is_active} onChange={(e) => setFormValues((p) => ({ ...p, is_active: e.target.checked }))} /></Flex>
              </FormControl>
              <FormControl>
                <FormLabel>Institution ID (optionnel)</FormLabel>
                <Input
                  type="number"
                  value={formValues.institution_id ?? ""}
                  onChange={(e) => setFormValues((p) => ({ ...p, institution_id: e.target.value ? parseInt(e.target.value, 10) : null }))}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Zone (sub_area_id) (optionnel)</FormLabel>
                <Input
                  type="number"
                  value={formValues.sub_area_id ?? ""}
                  onChange={(e) => setFormValues((p) => ({ ...p, sub_area_id: e.target.value ? parseInt(e.target.value, 10) : null }))}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Annuler</Button>
            <Button colorScheme="blue" onClick={handleSubmit} isLoading={isSubmitting}>Enregistrer</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={isConfirmOpen} leastDestructiveRef={cancelRef} onClose={onConfirmClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Supprimer le conducteur</AlertDialogHeader>
            <AlertDialogBody>Êtes-vous sûr de vouloir supprimer ce conducteur ?</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onConfirmClose}>Annuler</Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} isLoading={deletingId === activeItem?.id} ml={3}>Supprimer</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
